import { useMutation } from "@tanstack/react-query";
import { useDispatch } from 'react-redux';

import { AppConfig } from "../config/app.config";

import { api } from "./ApiClient";

import { CookiesHelper } from "../helpers/cookies.helper";
import { AuthHelper } from "../helpers/auth.helper";

import { setUserData } from "../store/misc.slice";

import { HMIResetPassword, HMISendPasswordResetEmail, HMISetPassword, HMISignIn, ISignInResponse } from "../types/services.interfaces/auth-cax";

export const useSignIn = () => {
    const dispatch = useDispatch();

    const mutationFn = async (data: HMISignIn): Promise<ISignInResponse> => {
        const res = await api.post('/auth-cax/sign-in', data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setProcessing(true);
            variables.setError(null);
        },
        onSuccess: (data) => {
            CookiesHelper.set(AppConfig.cookieTokenNames.auth, data.auth_token, (4 * 24 * 60 * 60 * 1000));
            dispatch(setUserData({ email: data.email }));

            window.location.href = '/';
        },
        onError: (error: any, variables) => {
            const errorMsg = error?.response?.data?.message ?? 'Something went wrong.';

            variables.setError(errorMsg);
        },
        onSettled: (_, __, variables) => {
            variables.setProcessing(false);
        },
    });
};

export const useSendPasswordResetEmail = () => {
    const mutationFn = async (data: HMISendPasswordResetEmail): Promise<string> => {
        const res = await api.patch('/auth-cax/send-password-reset-email', data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setProcessing(true);
            variables.setError(null);
        },
        onSuccess: (_, variables) => {
            variables.setSuccess(true);
        },
        onError: (error: any, variables) => {
            variables.setError(error?.response?.data?.message ?? 'Something went wrong.');
        },
        onSettled: (_, __, variables) => {
            variables.setProcessing(false);
        },
    });
};

export const useResetPassword = () => {
    const mutationFn = async (data: HMIResetPassword): Promise<string> => {
        AuthHelper.presetCustomRequest(api, data.token);

        const res = await api.patch('/auth-cax/reset-password', data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setProcessing(true);
            variables.setError(null);
        },
        onSuccess: (_, variables) => {
            variables.setSuccess(true);
        },
        onError: (error: any, variables) => {
            variables.setError(error?.response?.data?.message ?? 'Something went wrong.');
        },
        onSettled: (_, __, variables) => {
            variables.setProcessing(false);
        },
    });
};

export const useSetPassword = () => {
    const mutationFn = async (data: HMISetPassword): Promise<string> => {
        AuthHelper.presetCustomRequest(api, data.token);

        const res = await api.patch('/auth-cax/set-password', data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setProcessing(true);
            variables.setError(null);
        },
        onSuccess: (_, variables) => {
            variables.setSuccess(true);
        },
        onError: (error: any, variables) => {
            variables.setError(error?.response?.data?.message ?? 'Something went wrong.');
        },
        onSettled: (_, __, variables) => {
            variables.setProcessing(false);
        },
    });
};

export const useSignout = () => {
    AuthHelper.presetPrivateRequest(api);

    const dispatch = useDispatch();

    const mutationFn = async (): Promise<void> => {
        const res = await api.patch('/auth-cax/sign-out');
        return res.data;
    };

    return useMutation({
        mutationFn,
        onSettled: () => {
            CookiesHelper.remove(AppConfig.cookieTokenNames.auth);
            dispatch(setUserData(null));

            window.location.href = '/auth/sign-in';
        },
    });
};